import { createStore } from "solid-js/store";

import { MinPlatCoin, getCurPointType, isCrypto } from "../lib/pointUtils.ts";
import {
  CryptoChainCfg,
  CryptoConfItem,
  RealtimeFiatExchangeCurrRates,
} from "../types/crypto.ts";
import { sysConfigStore } from "./sysConfigStore.ts";
import { getDefaultPointType, getRewardPointType } from "./userStore.ts";

export const defaultPointCfg: CryptoConfItem = {
  point_type: 0,
  name: "",
  status: 0,
  sort: 0,
  point_ratio: 0,
  fiat_exchange_rate_mode: 0,
  fixed_fiat_exchange_rate: 0,
  default_fiat_exchange_rate: 0,
  min_usdt_exchange_rate: 0,
  max_usdt_exchange_rate: 0,
  clear_code_user_max_point: 0,
  pay_min_point: 0,
  put_min_point: 0,
  put_max_point: 0,
  icon: "",
  symbol: "",
  chain_cfg_list: [],
  remark: "",
  dipaly_point_ratio: 0,
  created_at: "",
  updated_at: "",
  chain_config_map: {},
};

type CryptoStore = {
  crypto_rate: RealtimeFiatExchangeCurrRates[];
  curCryptoConfig: CryptoConfItem;
  crypto_currency_config: CryptoConfItem[];
  fiat_currency_config: CryptoConfItem[];
  currency_config: Record<number, CryptoConfItem>;
  crypto_maps: Map<string | number, CryptoConfItem>;
  plat_coin_point_type: number;
};

export type FiatCurrency = CryptoConfItem;

export const [cryptoStore, setCryptoStore] = createStore<CryptoStore>({
  crypto_rate: [],
  curCryptoConfig: defaultPointCfg,
  crypto_currency_config: [],
  fiat_currency_config: [],
  currency_config: {},
  crypto_maps: new Map<string | number, CryptoConfItem>(),
  plat_coin_point_type: 0,
});

export const initCryptoData = (data: CryptoConfItem[]) => {
  initCryptoCfg(data);
  initCurCryptoConfig(Number(getCurPointType()));
};

const initCryptoCfg = function (crypto_currency_config: Array<CryptoConfItem>) {
  if (crypto_currency_config) {
    crypto_currency_config.sort((a: CryptoConfItem, b: CryptoConfItem) =>
      (a.sort || 0) < (b.sort || 0) ? -1 : 1,
    );
    const cryptoMaps = new Map<number, CryptoConfItem>();
    crypto_currency_config = crypto_currency_config?.filter(
      (el: CryptoConfItem) => {
        cryptoMaps.set(el.point_type, el);

        if (!el.status) {
          return false;
        }
        el.chain_cfg_list = el.chain_cfg_list || [];
        el.chain_cfg_list = el.chain_cfg_list?.filter(
          (temp: CryptoChainCfg) => !!temp.status,
        );

        return true;
      },
    );

    setCryptoStore("crypto_maps", cryptoMaps);
    const result = crypto_currency_config.reduce(
      (
        acc: { crypto: CryptoConfItem[]; fiat: CryptoConfItem[] },
        item: CryptoConfItem,
      ) => {
        if (isCrypto(item.point_type)) {
          acc.crypto.push(item);
        } else if (item.point_type >= MinPlatCoin) {
          setCryptoStore("plat_coin_point_type", item.point_type);
        } else {
          acc.fiat.push(item);
        }
        return acc;
      },
      { crypto: [], fiat: [] },
    );
    setCryptoStore("crypto_currency_config", result.crypto);
    setCryptoStore(
      "fiat_currency_config",
      result.fiat?.filter((item) => !item.disabled_wallet),
    );
    const currencyList = [
      ...cryptoStore.crypto_currency_config,
      ...cryptoStore.fiat_currency_config,
    ];
    setCryptoStore(
      "currency_config",
      currencyList.reduce(
        (acc, item) => {
          acc[item.point_type] = item;
          return acc;
        },
        {} as Record<number, CryptoConfItem>,
      ),
    );
  }
};

export const initCurCryptoConfig = (pointType: number) => {
  const config = cryptoStore.crypto_maps.get(pointType) || defaultPointCfg;
  setCryptoStore("curCryptoConfig", config);
};

// 获取汇率
export const initRealtimeFiatExchangeCurrRates = (
  data: RealtimeFiatExchangeCurrRates[],
) => {
  if (data && data.length > 0) {
    setCryptoStore("crypto_rate", data);
  }
};

export const getCryptoRate = (pointType: number) => {
  return (
    cryptoStore.crypto_rate.find((item) => item.pointType == pointType)?.rate ||
    1
  );
};

export const getConfigPropertyByPointTypeKey = (
  pointType: number = 5,
  key: keyof CryptoConfItem = "icon",
) => {
  return cryptoStore.crypto_maps.get(pointType)?.[key];
};

export const getCryptoRatio = function (pointType: number) {
  return getCrypto(pointType).point_ratio || 10000;
};

export const getPointFixed = function (pointType: number) {
  const crypto = cryptoStore.crypto_maps.get(pointType) || defaultPointCfg;
  let value = Number(Math.log10(crypto.dipaly_point_ratio)) || 0;
  if (value < 0) {
    value = 0;
  }
  return value;
};

export const calcPoint = (point: number, point_type: number) => {
  if (!point) return 0;
  if (point_type == null) point_type = getDefaultPointType() ?? 0;
  const ratio = getCryptoRatio(point_type);
  return point / ratio;
};

export const pointToPoint = (
  point_f: number,
  point_type_f: number,
  point_type_t: number,
  isConverted = false,
) => {
  if (!point_f) return 0;
  if (!isConverted) point_f = calcPoint(point_f, point_type_f);
  if (point_type_f === point_type_t) return point_f;
  const rate_f = getCryptoRate(point_type_f);
  const rate_t = getCryptoRate(point_type_t);

  return (point_f * rate_f) / rate_t;
};

export const pointToPointFormat = (
  point_f: number,
  point_type_f: number,
  point_type_t: number,
  isConverted = false,
) => {
  if (!point_f) return 0;
  const value = pointToPoint(point_f, point_type_f, point_type_t, isConverted);
  if (Math.floor(value) === value) return value;
  let valueStr = value.toString();
  const index = valueStr.indexOf(".");
  const fix = getPointFixed(point_type_t);
  valueStr = valueStr.substring(0, index + fix + 1);
  return parseFloat(valueStr);
};

export const pointToBase = (
  point: number,
  point_type: number,
  isConverted = false,
) => {
  return pointToPoint(
    point,
    point_type,
    sysConfigStore.point_type,
    isConverted,
  );
};

export const pointToBind = (
  point: number,
  point_type: number,
  isConverted = false,
) => {
  return pointToPoint(point, point_type, getDefaultPointType(), isConverted);
};

export const baseToBind = (point: number, isConverted = false) => {
  return pointToBind(point, sysConfigStore.point_type, isConverted);
};

export const baseToReward = (point: number, isConverted = false) => {
  return baseToPoint(point, getRewardPointType(), isConverted);
};

export const baseToPoint = (
  val: number,
  pointType: number,
  isConverted = false,
) => {
  if (!val) return 0;
  return pointToPoint(val, sysConfigStore.point_type, pointType, isConverted);
};

export const getCrypto = function (pointType: number) {
  return cryptoStore.crypto_maps.get(pointType) || defaultPointCfg;
};

export const getCryptoName = function (pointType: number) {
  return getCrypto(pointType).name || "";
};

// 获取货币简称/符号
export const getCryptoSymbol = function (pointType: number) {
  return getCrypto(pointType).symbol || "";
};

export const getCryptoIcon = function (pointType: number) {
  return getCrypto(pointType).icon || "";
};
export const basicCoinSwap = (
  point: number,
  pointType: number,
  isReverse = false,
  isConverted = true,
) => {
  if (!point) return 0;
  if (isReverse) {
    return baseToPoint(point, pointType, isConverted);
  } else {
    return pointToBind(point, pointType, isConverted);
  }
};
